import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Ban, Edit2, RotateCcw, Upload } from "lucide-react";
import { getEntityTypeFromPAN, formatCurrency } from "./constants";

const TableHead = ({ canEdit, selectAll, onSelectAll }) => (
  <thead>
    <tr>
      {canEdit && (
        <th className="w-10">
          <Checkbox
            checked={selectAll}
            onCheckedChange={onSelectAll}
            data-testid="select-all-checkbox"
          />
        </th>
      )}
      <th className="w-8"></th>
      <th>Date</th>
      <th>Party Name</th>
      <th>PAN</th>
      <th>Entity</th>
      <th>TDS Ledger</th>
      <th className="text-right">Amount</th>
      <th className="text-right" title="TDS amount from XML export">TDS (XML)</th>
      <th className="text-right" title="TDS amount from reference matching">TDS (Matched)</th>
      <th className="text-right">Rate</th>
      <th className="text-right">Expected</th>
      <th>Section</th>
      <th>Section (2025)</th>
      {canEdit && <th>Actions</th>}
    </tr>
  </thead>
);

const PartyNameCell = ({ txn, canEdit, isExcluded, onOpenPartyNameModal }) => {
  const reversalBadge = txn.is_reversal ? (
    <Badge className="ml-2 text-xs bg-rose-100 text-rose-700 border-rose-200" data-testid={`reversal-badge-${txn.id}`}>
      Reversal · Needs Approval
    </Badge>
  ) : null;
  if (txn.party_name) {
    return (
      <>
        {canEdit ? (
          <span
            className="cursor-pointer hover:underline"
            onClick={() => onOpenPartyNameModal(txn)}
            title="Click to update party name"
          >
            {txn.party_name}
          </span>
        ) : (
          <span>{txn.party_name}</span>
        )}
        {isExcluded && !txn.is_reversal && <Badge variant="outline" className="ml-2 text-xs">Excluded</Badge>}
        {reversalBadge}
      </>
    );
  }
  return canEdit ? (
    <button
      className="text-orange-500 text-xs font-medium hover:underline cursor-pointer bg-transparent border-none"
      onClick={() => onOpenPartyNameModal(txn)}
      title="Click to add party name"
    >
      NO PARTY NAME - Click to add
    </button>
  ) : (
    <span className="text-orange-500 text-xs font-medium">NO PARTY NAME</span>
  );
};

const PanCell = ({ txn, canEdit, onOpenPanModal }) => {
  if (txn.pan) {
    return canEdit ? (
      <span
        className="cursor-pointer hover:underline"
        onClick={() => onOpenPanModal(txn.party_name, txn.pan)}
        title="Click to update PAN"
      >
        {txn.pan}
      </span>
    ) : (
      <span>{txn.pan}</span>
    );
  }
  return canEdit ? (
    <button
      className="text-red-500 text-xs font-medium hover:underline cursor-pointer bg-transparent border-none"
      onClick={() => onOpenPanModal(txn.party_name, "")}
      title="Click to add PAN"
    >
      NO PAN (20%) - Click to add
    </button>
  ) : (
    <span className="text-red-500 text-xs font-medium">NO PAN (20%)</span>
  );
};

const EntityCell = ({ pan }) => {
  const entityType = getEntityTypeFromPAN(pan);
  if (entityType) {
    return <Badge className={`${entityType.color} text-xs`}>{entityType.label}</Badge>;
  }
  return pan ? (
    <Badge variant="outline" className="text-xs">Unknown</Badge>
  ) : (
    <Badge className="bg-red-100 text-red-800 text-xs">No PAN</Badge>
  );
};

const AmountCell = ({ txn, canEdit, onOpenTdsModal }) => {
  const isZeroAmountWithTds = txn.amount === 0 && txn.tds_amount > 0;
  if (canEdit) {
    return (
      <span
        className={`cursor-pointer hover:underline hover:text-blue-600 ${
          isZeroAmountWithTds ? "text-red-600 font-bold" : ""
        }`}
        onClick={() => onOpenTdsModal(txn)}
        title={isZeroAmountWithTds ? "Zero amount with TDS - Click to edit" : "Click to edit amount & TDS"}
      >
        {formatCurrency(txn.amount)}
        {isZeroAmountWithTds && <span className="ml-1 text-red-500" title="Amount is zero but TDS exists">!</span>}
      </span>
    );
  }
  return <span className={isZeroAmountWithTds ? "text-red-600 font-bold" : ""}>{formatCurrency(txn.amount)}</span>;
};

const TdsMatchedCell = ({ txn, canEdit, onOpenTdsModal }) => {
  const inner = txn.tds_amount_matched != null ? (
    <span className="flex items-center gap-1">
      {formatCurrency(txn.tds_amount_matched)}
      {txn.tds_amount_xml != null && txn.tds_amount_matched !== txn.tds_amount_xml && (
        <span className="text-xs text-blue-500" title="Differs from XML">*</span>
      )}
    </span>
  ) : (
    <span className="text-slate-400">{formatCurrency(txn.tds_amount)}</span>
  );
  if (canEdit) {
    return (
      <span
        className="cursor-pointer hover:underline hover:text-blue-600"
        onClick={() => onOpenTdsModal(txn)}
        title="Click to edit TDS amount"
      >
        {inner}
      </span>
    );
  }
  return txn.tds_amount_matched != null ? formatCurrency(txn.tds_amount_matched) : formatCurrency(txn.tds_amount);
};

const SectionCell = ({ txn }) => {
  const effectiveSection = txn.user_section || txn.section;
  return (
    <div className="flex items-center gap-1">
      {effectiveSection ? <span className="section-badge">{effectiveSection}</span> : "-"}
      {txn.user_section && txn.user_section !== txn.section && (
        <span className="text-xs text-blue-500" title={`Original: ${txn.section}`}>*</span>
      )}
    </div>
  );
};

const ActionsCell = ({ txn, isExcluded, onEditSection, onRestore, onExclude }) => (
  <div className="flex gap-1">
    <Button
      size="sm"
      variant="ghost"
      className="h-7 w-7 p-0"
      onClick={() => onEditSection(txn, false)}
      title="Edit Section"
      data-testid={`edit-section-${txn.id}`}
    >
      <Edit2 className="w-3 h-3" />
    </Button>
    {isExcluded ? (
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0 text-green-600"
        onClick={() => onRestore(txn.id)}
        title={txn.is_reversal ? "Approve reversal (include in totals)" : "Restore"}
        data-testid={`restore-txn-${txn.id}`}
      >
        <RotateCcw className="w-3 h-3" />
      </Button>
    ) : (
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0 text-red-500"
        onClick={() => onExclude(txn, false)}
        title="Exclude"
        data-testid={`exclude-txn-${txn.id}`}
      >
        <Ban className="w-3 h-3" />
      </Button>
    )}
  </div>
);

const TransactionRow = ({
  txn,
  canEdit,
  selectedTxns,
  handleSelectTxn,
  handlers,
}) => {
  const isExcluded = txn.is_excluded;
  const hasMismatch = txn.rate_mismatch && !isExcluded;
  const isSelected = selectedTxns.has(txn.id);

  return (
    <tr
      className={`${isExcluded ? "opacity-50 bg-slate-50" : ""} ${hasMismatch ? "bg-amber-50" : ""} ${
        isSelected ? "bg-blue-50" : ""
      }`}
    >
      {canEdit && (
        <td className="text-center">
          {!isExcluded && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => handleSelectTxn(txn.id, checked)}
              data-testid={`select-txn-${txn.id}`}
            />
          )}
        </td>
      )}
      <td className="text-center">
        {hasMismatch && <AlertTriangle className="w-4 h-4 text-amber-500" title={txn.mismatch_reason} />}
        {isExcluded && !txn.is_reversal && <Ban className="w-4 h-4 text-slate-400" title={txn.exclusion_reason} />}
        {txn.is_reversal && <AlertTriangle className="w-4 h-4 text-rose-500" title={txn.exclusion_reason || "Reversal pending approval"} />}
      </td>
      <td className="font-mono text-sm">{txn.date}</td>
      <td className="font-medium">
        <PartyNameCell
          txn={txn}
          canEdit={canEdit}
          isExcluded={isExcluded}
          onOpenPartyNameModal={handlers.openPartyNameModal}
        />
      </td>
      <td className="pan-cell">
        <PanCell txn={txn} canEdit={canEdit} onOpenPanModal={handlers.openPanModal} />
      </td>
      <td>
        <EntityCell pan={txn.pan} />
      </td>
      <td className="text-sm">{txn.tds_ledger}</td>
      <td className="amount-cell">
        <AmountCell txn={txn} canEdit={canEdit} onOpenTdsModal={handlers.openTdsModal} />
      </td>
      <td className="amount-cell text-slate-500" title="TDS from XML">
        {txn.tds_amount_xml != null ? formatCurrency(txn.tds_amount_xml) : "-"}
      </td>
      <td className="amount-cell text-emerald-600" title="TDS from reference match">
        <TdsMatchedCell txn={txn} canEdit={canEdit} onOpenTdsModal={handlers.openTdsModal} />
      </td>
      <td className={`amount-cell ${hasMismatch ? "text-amber-600 font-bold" : ""}`}>
        {txn.tds_rate?.toFixed(2)}%
      </td>
      <td className="amount-cell text-slate-500">{txn.expected_rate?.toFixed(2) || "-"}%</td>
      <td>
        <SectionCell txn={txn} />
      </td>
      <td className="text-xs text-slate-600" title={txn.new_section || ''}>
        {txn.new_section || '-'}
      </td>
      {canEdit && (
        <td>
          <ActionsCell
            txn={txn}
            isExcluded={isExcluded}
            onEditSection={handlers.openSectionModal}
            onRestore={handlers.handleRestore}
            onExclude={handlers.openExclusionModal}
          />
        </td>
      )}
    </tr>
  );
};

export const TransactionTable = ({
  filteredTransactions,
  loading,
  canEdit,
  selectedTxns,
  selectAll,
  handleSelectAll,
  handleSelectTxn,
  handlers,
}) => (
  <Card className="data-card">
    <CardHeader className="data-card-header">
      <CardTitle className="data-card-title">TDS Transactions ({filteredTransactions.length})</CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : filteredTransactions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="tds-table">
            <TableHead canEdit={canEdit} selectAll={selectAll} onSelectAll={handleSelectAll} />
            <tbody>
              {filteredTransactions.map((txn) => (
                <TransactionRow
                  key={txn.id}
                  txn={txn}
                  canEdit={canEdit}
                  selectedTxns={selectedTxns}
                  handleSelectTxn={handleSelectTxn}
                  handlers={handlers}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Upload className="empty-state-icon" />
          <p className="empty-state-title">No transactions</p>
        </div>
      )}
    </CardContent>
  </Card>
);
